import axios from "axios";
import {queryConfig} from "@/config/api.query.config.ts";
import {DateTimeHelper} from "@/utils/dateTimeHelper.ts";

const normalizeEnvUrl = (value?: string): string | undefined => {
    const trimmed = value?.trim();
    return trimmed || undefined;
};

const deriveHealthCheckUrl = (apiBaseUrl?: string): string | undefined => {
    if (!apiBaseUrl) {
        return undefined;
    }

    try {
        const url = new URL(apiBaseUrl);
        return `${url.origin}/health`;
    } catch {
        return undefined;
    }
};

const deriveMaintenanceStatusUrl = (apiBaseUrl?: string): string | undefined => {
    if (!apiBaseUrl) {
        return undefined;
    }

    try {
        const url = new URL(apiBaseUrl);
        return `${url.origin}/maintenance/status`;
    } catch {
        return undefined;
    }
};

const urlConfig = {
    api: import.meta.env.VITE_API_BASE_URL,
    notificationsHub: import.meta.env.VITE_NOTIFICATIONS_HUB_URL,
    forgetPasswordCallbackUrl: import.meta.env.VITE_FORGET_PASSWORD_URL,
    healthCheckUrl:
        normalizeEnvUrl(import.meta.env.VITE_SERVER_HEALTH_CHECK_URL) ??
        deriveHealthCheckUrl(normalizeEnvUrl(import.meta.env.VITE_API_BASE_URL)),
    maintenanceStatusUrl: deriveMaintenanceStatusUrl(
        normalizeEnvUrl(import.meta.env.VITE_API_BASE_URL),
    ),
};

const offset = new Date().getTimezoneOffset(); // UTC offset in minutes
const clientTimeShift = offset / -60; // Convert to hours

export const ApiConfig = {
    currentUrls: urlConfig,
    url: urlConfig.api,
    notificationsHub: urlConfig.notificationsHub,
    queries: queryConfig,
    axiosInstance: axios.create(),
    unAuthenticatedEntryPath: '/auth/login',
    redirect_url_key: 'redirectUrl',
    healthCheckUrl: urlConfig.healthCheckUrl,
    maintenanceStatusUrl: urlConfig.maintenanceStatusUrl,
};

const api = axios.create({
    withCredentials: true, // Important for cookies
    headers: {
        'Content-Type': 'application/json',
        'Accept-Language': 'en',
        'X-Client-TimeShift': clientTimeShift,
    },
    baseURL: ApiConfig.currentUrls.api,
    transformResponse: [
        (data) => {
            if (!data) return data;

            if (data instanceof Blob) return data;

            const parsedData = JSON.parse(data);
            return DateTimeHelper.parseDates(parsedData);
        },
    ],
});

// Note: 401 handling (token refresh + retry, sign-out on failure) is owned by the
// interceptor registered in useAxiosAuth. A naive retry here would set `_retry` and
// short-circuit that refresh logic, so it is intentionally not registered.

ApiConfig.axiosInstance = api;
